// BibliographyTabPanel — right-rail tab scoped to the active piece. Lists this
// piece's source-type references, lets the user flip each to "Background
// reading only", and opens the existing BibliographyPickerDialog to add more.
// Also exposes a per-piece citation-style override (B4): persisted to
// `content_pieces.metadata.citationStyle` so it cascades into the citations
// preview without a schema change.
import { useEffect, useMemo, useState } from "react";
import { BookMarked, Copy, Download, Loader2, Plus } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { BibliographyPickerDialog } from "@/components/composer/draft/BibliographyPickerDialog";
import { CitationStylePicker } from "@/components/settings/CitationStylePicker";
import {
  DEFAULT_CITATION_STYLE,
  getCitationStyleLabel,
  resolveCslStyleId,
  type CitationStyle,
} from "@/lib/citationStyles";
import {
  readBriefCitationStyleOverride,
  setBriefCitationStyle,
} from "@/lib/composer/core/pieces/briefs";
import {
  CITATION_EXPORT_EXTENSION,
  CITATION_EXPORT_LABELS,
  CITATION_EXPORT_MIME,
  exportBibliography,
  type CitationExportFormat,
} from "@/lib/citationExport";
import { useBibliographyEntries } from "./useBibliographyEntries";
import { BibliographyReferenceRow } from "./BibliographyReferenceRow";

const STYLE_ID_RE = /^[a-z0-9][a-z0-9-]{0,127}$/i;
const isStyleId = (v: string | null | undefined): v is string =>
  !!v && STYLE_ID_RE.test(v);

const PIECE_DEFAULT_VALUE = "__workspace_default__";

type Props = {
  workspaceId: string | null | undefined;
  pieceId: string | null | undefined;
  /**
   * Called after any add/toggle/remove so callers can refetch the composer
   * snapshot (so CitationsPreviewPanel and other consumers re-render with the
   * new cited flags).
   */
  onChanged?: () => void | Promise<void>;
};

export const BibliographyTabPanel = ({ workspaceId, pieceId, onChanged }: Props) => {
  const wsId = workspaceId ?? null;
  const pcId = pieceId ?? null;
  const { entries, loading, refetch, remove } = useBibliographyEntries(wsId, pcId);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [workspaceStyle, setWorkspaceStyle] = useState<CitationStyle>(DEFAULT_CITATION_STYLE);
  const [pieceOverride, setPieceOverride] = useState<CitationStyle | null>(null);
  const [savingStyle, setSavingStyle] = useState(false);

  const notifyChanged = () => {
    void onChanged?.();
  };

  // Workspace default
  useEffect(() => {
    if (!wsId) return;
    let cancelled = false;
    (async () => {
      const { data } = await supabase
        .from("profiles")
        .select("default_citation_style")
        .eq("id", wsId)
        .maybeSingle();
      if (cancelled) return;
      const raw = (data as { default_citation_style?: string | null } | null)?.default_citation_style;
      setWorkspaceStyle(isStyleId(raw) ? resolveCslStyleId(raw) : DEFAULT_CITATION_STYLE);
    })();
    return () => { cancelled = true; };
  }, [wsId]);

  // Per-piece override
  useEffect(() => {
    if (!wsId || !pcId) {
      setPieceOverride(null);
      return;
    }
    let cancelled = false;
    (async () => {
      const { data } = await supabase
        .from("content_pieces")
        .select("metadata")
        .eq("id", pcId)
        .maybeSingle();
      if (cancelled) return;
      const raw = readBriefCitationStyleOverride((data as { metadata?: unknown } | null)?.metadata as never);
      setPieceOverride(isStyleId(raw) ? resolveCslStyleId(raw) : null);
    })();
    return () => { cancelled = true; };
  }, [wsId, pcId]);

  

  const handleStyleChange = async (value: string) => {
    if (!wsId || !pcId) return;
    const next: CitationStyle | null = value === PIECE_DEFAULT_VALUE ? null : value;
    const prev = pieceOverride;
    setPieceOverride(next);
    setSavingStyle(true);
    try {
      await setBriefCitationStyle(wsId, pcId, next);
      notifyChanged();
    } catch (e) {
      setPieceOverride(prev);
      toast.error((e as Error).message);
    } finally {
      setSavingStyle(false);
    }
  };

  const exportableItems = useMemo(
    () => entries.map((e) => e.item).filter((it): it is NonNullable<typeof it> => it != null),
    [entries],
  );

  const triggerDownload = (text: string, mime: string, filename: string) => {
    const blob = new Blob([text], { type: mime });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleExportDownload = (format: CitationExportFormat) => {
    if (exportableItems.length === 0) {
      toast.error("No bibliography entries to export.");
      return;
    }
    try {
      const text = exportBibliography(exportableItems, format);
      triggerDownload(
        text,
        CITATION_EXPORT_MIME[format],
        `bibliography.${CITATION_EXPORT_EXTENSION[format]}`,
      );
    } catch (e) {
      toast.error((e as Error).message);
    }
  };

  const handleExportCopy = async (format: CitationExportFormat) => {
    if (exportableItems.length === 0) {
      toast.error("No bibliography entries to export.");
      return;
    }
    try {
      const text = exportBibliography(exportableItems, format);
      await navigator.clipboard.writeText(text);
      toast.success(`Copied ${CITATION_EXPORT_LABELS[format]} to clipboard.`);
    } catch (e) {
      toast.error((e as Error).message);
    }
  };

  if (!wsId) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-2 p-6 text-center text-sm text-muted-foreground">
        <BookMarked className="h-5 w-5" />
        <p>Select a workspace to see this piece's bibliography.</p>
      </div>
    );
  }

  if (!pcId) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-2 p-6 text-center text-sm text-muted-foreground">
        <BookMarked className="h-5 w-5" />
        <p>Open a piece to see its bibliography.</p>
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col">
      <div className="space-y-2 border-b border-border/60 p-3">
        <div className="flex items-center justify-between gap-2">
          <h3 className="text-sm font-semibold text-foreground">Bibliography for this piece</h3>
          <div className="flex items-center gap-1.5">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  size="sm"
                  variant="ghost"
                  className="h-7 gap-1.5 px-2 text-xs"
                  disabled={exportableItems.length === 0}
                  title="Export bibliography"
                >
                  <Download className="h-3.5 w-3.5" />
                  Export
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel className="text-xs">Download</DropdownMenuLabel>
                {(Object.keys(CITATION_EXPORT_LABELS) as CitationExportFormat[]).map((f) => (
                  <DropdownMenuItem
                    key={`dl-${f}`}
                    onClick={() => handleExportDownload(f)}
                    className="text-xs"
                  >
                    <Download className="mr-2 h-3.5 w-3.5" />
                    {CITATION_EXPORT_LABELS[f]}
                  </DropdownMenuItem>
                ))}
                <DropdownMenuSeparator />
                <DropdownMenuLabel className="text-xs">Copy to clipboard</DropdownMenuLabel>
                {(Object.keys(CITATION_EXPORT_LABELS) as CitationExportFormat[]).map((f) => (
                  <DropdownMenuItem
                    key={`cp-${f}`}
                    onClick={() => void handleExportCopy(f)}
                    className="text-xs"
                  >
                    <Copy className="mr-2 h-3.5 w-3.5" />
                    {CITATION_EXPORT_LABELS[f]}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
            <Button
              size="sm"
              variant="secondary"
              onClick={() => setPickerOpen(true)}
              className="h-7 gap-1.5 px-2 text-xs"
            >
              <Plus className="h-3.5 w-3.5" />
              Add entries
            </Button>
          </div>
        </div>
        <p className="text-xs leading-snug text-muted-foreground">
          Everything here prints in the back matter. Toggle items off if they're only background
          reading and shouldn't get an inline citation.
        </p>
        <div className="flex items-center gap-2 pt-1">
          <Label htmlFor={`piece-citestyle-${pcId}`} className="text-xs text-muted-foreground">
            Style for this piece
          </Label>
          <div id={`piece-citestyle-${pcId}`} className="min-w-0 flex-1">
            <CitationStylePicker
              value={pieceOverride ?? PIECE_DEFAULT_VALUE}
              onChange={handleStyleChange}
              workspaceId={wsId}
              disabled={savingStyle}
              triggerClassName="h-7 w-full text-xs"
              extraTopOption={{
                value: PIECE_DEFAULT_VALUE,
                label: `Workspace default (${getCitationStyleLabel(workspaceStyle)})`,
              }}
              triggerLabel={
                pieceOverride
                  ? getCitationStyleLabel(pieceOverride)
                  : `Workspace default (${getCitationStyleLabel(workspaceStyle)})`
              }
            />
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-auto p-3">
        {loading ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading…
          </div>
        ) : entries.length === 0 ? (
          <div className="rounded-md border border-dashed border-border p-4 text-center text-sm text-muted-foreground">
            No bibliography entries yet. Add entries from your My Library.
          </div>
        ) : (
          <ul className="space-y-2">
            {entries.map((entry) => (
              <BibliographyReferenceRow
                key={entry.id}
                entry={entry}
                onRemove={async () => {
                  await remove(entry.id);
                  notifyChanged();
                }}
              />
            ))}
          </ul>
        )}
      </div>

      <BibliographyPickerDialog
        open={pickerOpen}
        onOpenChange={setPickerOpen}
        workspaceId={wsId}
        pieceId={pcId}
        onInserted={() => {
          refetch();
          notifyChanged();
        }}
        defaultCited={false}
      />
    </div>
  );
};
